import React, { useState, useMemo, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Chip,
  Paper,
  IconButton,
  Divider,
  Alert,
  Collapse,
} from '@mui/material';
import {
  PlayArrow as ConnectIcon,
  Stop as DisconnectIcon,
  Refresh as ReconnectIcon,
  Send as SendIcon,
  Delete as DeleteIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  ContentCopy as CopyIcon,
} from '@mui/icons-material';
import { miaChatV2SocketService } from 'app/NewAIAvatarLayout/features/MiaChat/socketService/miaChatSocketService';
import { socketServiceManager } from 'app/socketService';
import { DEBUG_COLORS } from '../config';
import { useNotification } from '../context/NotificationContext';

interface SocketEvent {
  event: string;
  data: any;
  timestamp: number;
  type: 'sent' | 'received';
}

export const SocketManagement: React.FC = () => {
  const { updateCount } = useNotification();
  // Get existing socket service instance (singleton)
  const socketServiceRef = useRef<any>(null);
  const [isConnected, setIsConnected] = useState(false);
  const [connectionError, setConnectionError] = useState<string | null>(null);
  const [eventHistory, setEventHistory] = useState<any[]>([]);
  
  // Get default socket URL from environment
  const defaultSocketUrl = process.env.REACT_APP_WS_URL || '';
  const [eventName, setEventName] = useState('subscribe');
  const [eventPayload, setEventPayload] = useState('"02db5a03-2368-4563-a736-8f7533cee11e"');
  const [activeConversationId, setActiveConversationId] = useState<string>('');

  // Get active conversation ID from socket service
  useEffect(() => {
    if (socketServiceRef.current) {
      try {
        const activeSubscriptions = socketServiceRef.current.getActiveSubscriptions?.() || [];
        if (activeSubscriptions.length > 0) {
          // Use the first active subscription as the active conversation ID
          setActiveConversationId(activeSubscriptions[0]);
        } else {
          // Try to get from event history (look for recent subscribe events)
          const history = socketServiceRef.current.getEventHistory() || [];
          const subscribeEvents = history
            .filter((evt: any) => evt.event === 'emit:subscribe' || evt.event === 'subscribe')
            .map((evt: any) => {
              // Subscribe event payload is just the conversation ID string
              return typeof evt.data === 'string' ? evt.data : evt.data?.conversationId || evt.data?.conversation_id;
            })
            .filter((id: any) => id && typeof id === 'string');
          
          if (subscribeEvents.length > 0) {
            setActiveConversationId(subscribeEvents[subscribeEvents.length - 1]);
          }
        }
      } catch (error) {
        console.error('Error getting active conversation ID:', error);
      }
    }
  }, [socketServiceRef.current, eventHistory]);

  // Update payload when active conversation ID changes (only if payload is empty or default)
  useEffect(() => {
    if ((eventName === 'subscribe' || eventName === 'unsubscribe') && activeConversationId) {
      const currentPayload = eventPayload.trim();
      // Only update if payload is empty, default, or doesn't contain a valid ID
      if (!currentPayload || 
          currentPayload === '"02db5a03-2368-4563-a736-8f7533cee11e"' ||
          currentPayload === '02db5a03-2368-4563-a736-8f7533cee11e') {
        setEventPayload(`"${activeConversationId}"`);
      }
    }
  }, [activeConversationId]);
  
  // Predefined event suggestions with their payloads
  const eventSuggestions = useMemo(() => {
    const conversationId = activeConversationId || '02db5a03-2368-4563-a736-8f7533cee11e';
    return [
      {
        name: 'subscribe',
        label: 'Subscribe',
        payload: conversationId,
        description: `Subscribe to conversation: ${conversationId}`
      },
      {
        name: 'unsubscribe',
        label: 'Unsubscribe',
        payload: conversationId,
        description: `Unsubscribe from conversation: ${conversationId}`
      },
    ];
  }, [activeConversationId]);
  
  const handleSuggestionClick = (suggestion: typeof eventSuggestions[0]) => {
    setEventName(suggestion.name);
    setEventPayload(suggestion.payload);
  };
  const [expandedEvents, setExpandedEvents] = useState<Set<number>>(new Set());
  const [eventFilter, setEventFilter] = useState<string>('all');

  // Initialize socket service
  useEffect(() => {
    try {
      // Try to get existing instance, or create one if it doesn't exist
      if (defaultSocketUrl) {
        const config = {
          url: defaultSocketUrl,
          autoConnect: false, // Don't auto-connect, let user control it
          reconnection: true,
          reconnectionAttempts: 5,
          reconnectionDelay: 1000,
          options: {
            path: '/ws/',
            transports: ['websocket', 'polling']
          }
        };
        
        socketServiceRef.current = miaChatV2SocketService(config);
        setIsConnected(socketServiceRef.current.isConnected());
        setEventHistory(socketServiceRef.current.getEventHistory() || []);
      }
    } catch (error: any) {
      console.error('Failed to initialize socket service:', error);
      setConnectionError(error.message || 'Failed to initialize socket service');
    }
  }, []);

  // Monitor connection status and event history
  useEffect(() => {
    if (!socketServiceRef.current) return;

    const interval = setInterval(() => {
      if (socketServiceRef.current) {
        setIsConnected(socketServiceRef.current.isConnected());
        const history = socketServiceRef.current.getEventHistory() || [];
        setEventHistory(history);
        // Update notification count
        updateCount('socket', history.length);
      }
    }, 500); // Update every 500ms

    return () => clearInterval(interval);
  }, []);

  // Combine sent and received events
  const allEvents = useMemo(() => {
    const events: SocketEvent[] = [];
    
    // Add events from history
    eventHistory.forEach((evt) => {
      // Check if it's an emitted event (starts with 'emit:') or received event
      const isSent = evt.event.startsWith('emit:');
      events.push({
        event: isSent ? evt.event.replace('emit:', '') : evt.event,
        data: evt.data,
        timestamp: evt.timestamp,
        type: isSent ? 'sent' : 'received',
      });
    });

    return events.sort((a, b) => b.timestamp - a.timestamp);
  }, [eventHistory]);

  const filteredEvents = useMemo(() => {
    if (eventFilter === 'all') return allEvents;
    return allEvents.filter((evt) => evt.type === eventFilter);
  }, [allEvents, eventFilter]);

  const handleConnect = () => {
    try {
      setConnectionError(null);
      
      // Get or initialize global socket
      const globalSocket = socketServiceManager.getGlobalSocket();
      
      if (globalSocket) {
        // If global socket exists but is disconnected, connect it
        if (!globalSocket.connected) {
          globalSocket.connect();
          console.log('🔌 Connecting global socket...');
        }
      } else {
        // Initialize global socket if it doesn't exist
        if (defaultSocketUrl) {
          const config = {
            url: defaultSocketUrl,
            autoConnect: true,
            reconnection: true,
            options: {
              path: '/ws/',
              transports: ['websocket', 'polling'],
              reconnection: true,
              reconnectionAttempts: 10,
              reconnectionDelay: 10000,
              reconnectionDelayMax: 30000,
              reconnectionAttemptsMax: 10,
            }
          };
          socketServiceManager.initializeGlobalSocket(config);
          console.log('🔌 Initializing and connecting global socket...');
        } else {
          setConnectionError('Socket URL not configured');
          return;
        }
      }
      
      // Also call service's connect method to set up event listeners
      if (socketServiceRef.current) {
        socketServiceRef.current.connect();
      }
      
      // Update connection status after a short delay to allow connection to establish
      setTimeout(() => {
        const currentGlobalSocket = socketServiceManager.getGlobalSocket();
        if (currentGlobalSocket) {
          setIsConnected(currentGlobalSocket.connected);
        } else if (socketServiceRef.current) {
          setIsConnected(socketServiceRef.current.isConnected());
        }
      }, 100);
    } catch (error: any) {
      setConnectionError(error.message || 'Failed to connect');
      console.error('Error connecting socket:', error);
    }
  };

  const handleDisconnect = () => {
    try {
      // Disconnect the global socket instead of just unsubscribing
      const globalSocket = socketServiceManager.getGlobalSocket();
      if (globalSocket) {
        globalSocket.disconnect();
        console.log('🔌 Global socket disconnected');
      }
      
      // Also disconnect the service instance
      if (socketServiceRef.current) {
        socketServiceRef.current.disconnect();
      }
      
      setIsConnected(false);
      setConnectionError(null);
    } catch (error: any) {
      setConnectionError(error.message || 'Failed to disconnect');
      console.error('Error disconnecting socket:', error);
    }
  };

  const handleReconnect = () => {
    if (!socketServiceRef.current) return;
    
    try {
      handleDisconnect();
      setTimeout(() => {
        handleConnect();
      }, 500);
    } catch (error: any) {
      setConnectionError(error.message || 'Failed to reconnect');
    }
  };

  const handleEmitEvent = () => {
    if (!socketServiceRef.current) {
      alert('Socket service not initialized');
      return;
    }

    if (!isConnected) {
      alert('Socket is not connected. Please connect first.');
      return;
    }

    if (!eventName.trim()) {
      alert('Please enter an event name.');
      return;
    }

    try {
      // For subscribe/unsubscribe, payload is just the conversation ID string, not JSON
      let payload: any = null;
      if (eventPayload.trim()) {
        // Try to parse as JSON first, if it fails, use as string
        try {
          const parsed = JSON.parse(eventPayload);
          // If it's an object with conversationId, extract it
          if (typeof parsed === 'object' && parsed !== null && parsed.conversationId) {
            payload = parsed.conversationId;
          } else if (typeof parsed === 'string') {
            payload = parsed;
          } else {
            payload = parsed;
          }
        } catch {
          // If not valid JSON, remove quotes if present and use as string (conversation ID)
          const trimmed = eventPayload.trim();
          payload = trimmed.startsWith('"') && trimmed.endsWith('"') 
            ? trimmed.slice(1, -1) 
            : trimmed;
        }
      }
      
      socketServiceRef.current.emit(eventName.trim(), payload);
      
      // Update event history immediately
      setEventHistory(socketServiceRef.current.getEventHistory() || []);
      
      // Don't clear payload after sending - keep the state as is
    } catch (error: any) {
      alert(`Invalid JSON payload: ${error.message}`);
    }
  };

  const handleClearHistory = () => {
    if (!socketServiceRef.current) return;
    
    try {
      socketServiceRef.current.clearEventHistory();
      setEventHistory([]);
    } catch (error: any) {
      console.error('Failed to clear event history:', error);
    }
  };

  const toggleExpand = (index: number) => {
    setExpandedEvents((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(index)) {
        newSet.delete(index);
      } else {
        newSet.add(index);
      }
      return newSet;
    });
  };

  const copyEventData = async (event: SocketEvent) => {
    try {
      const dataStr = JSON.stringify(event.data, null, 2);
      await navigator.clipboard.writeText(dataStr);
    } catch (error) {
      console.error('Failed to copy event data:', error);
    }
  };

  const getEventColor = (type: 'sent' | 'received'): string => {
    return type === 'sent' ? DEBUG_COLORS.info : DEBUG_COLORS.success;
  };

  const formatTimestamp = (timestamp: number): string => {
    return new Date(timestamp).toLocaleTimeString();
  };

  const formatData = (data: any): string => {
    if (!data) return 'No data';
    try {
      return JSON.stringify(data, null, 2);
    } catch {
      return String(data);
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {/* Connection Status */}
      <Paper
        sx={{
          p: 2,
          bgcolor: isConnected ? 'success.light' : 'error.light',
          border: '1px solid',
          borderColor: isConnected ? 'success.main' : 'error.main',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
              Connection Status
            </Typography>
            <Chip
              label={isConnected ? 'Connected' : 'Disconnected'}
              color={isConnected ? 'success' : 'error'}
              size="small"
              sx={{ mr: 1 }}
            />
            {defaultSocketUrl && (
              <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                {defaultSocketUrl}
              </Typography>
            )}
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="contained"
              size="small"
              startIcon={<ConnectIcon />}
              onClick={handleConnect}
              disabled={isConnected}
              color="success"
            >
              Connect
            </Button>
            <Button
              variant="contained"
              size="small"
              startIcon={<DisconnectIcon />}
              onClick={handleDisconnect}
              disabled={!isConnected}
              color="error"
            >
              Disconnect
            </Button>
            <Button
              variant="outlined"
              size="small"
              startIcon={<ReconnectIcon />}
              onClick={handleReconnect}
              disabled={!socketServiceRef.current}
            >
              Reconnect
            </Button>
          </Box>
        </Box>
        {connectionError && (
          <Alert severity="error" sx={{ mt: 1 }}>
            {connectionError}
          </Alert>
        )}
      </Paper>

      {/* Connection Info */}
      {defaultSocketUrl && (
        <Paper sx={{ p: 2, border: '1px solid', borderColor: 'divider' }}>
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
            Socket Configuration
          </Typography>
          <Typography variant="body2" color="text.secondary">
            URL: {defaultSocketUrl}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
            Using existing socket service connection
          </Typography>
        </Paper>
      )}

      {/* Emit Event Section */}
      <Paper sx={{ p: 2, border: '1px solid', borderColor: 'divider' }}>
        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
          Emit Event
        </Typography>
        
        {/* Event Suggestions */}
        <Box sx={{ mb: 2 }}>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
            Quick Actions:
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {eventSuggestions.map((suggestion) => (
              <Chip
                key={suggestion.name}
                label={suggestion.label}
                onClick={() => handleSuggestionClick(suggestion)}
                color={eventName === suggestion.name ? 'primary' : 'default'}
                variant={eventName === suggestion.name ? 'filled' : 'outlined'}
                size="small"
                sx={{
                  cursor: 'pointer',
                  '&:hover': {
                    bgcolor: eventName === suggestion.name ? 'primary.dark' : 'action.hover',
                  },
                }}
                title={suggestion.description}
              />
            ))}
          </Box>
        </Box>
        
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          <TextField
            fullWidth
            size="small"
            label="Event Name"
            placeholder="e.g., subscribe, unsubscribe"
            value={eventName}
            onChange={(e) => setEventName(e.target.value)}
            disabled={!isConnected}
          />
          <TextField
            fullWidth
            multiline
            rows={4}
            label={eventName === 'subscribe' || eventName === 'unsubscribe' ? 'Conversation ID' : 'Payload (JSON)'}
            placeholder={eventName === 'subscribe' || eventName === 'unsubscribe' ? '"02db5a03-2368-4563-a736-8f7533cee11e"' : '{"key": "value"}'}
            value={eventPayload}
            onChange={(e) => setEventPayload(e.target.value)}
            disabled={!isConnected}
            helperText={eventName === 'subscribe' || eventName === 'unsubscribe' 
              ? 'Enter conversation ID (string, not JSON)' 
              : 'Enter JSON payload'}
            sx={{
              fontFamily: 'monospace',
              '& .MuiInputBase-input': {
                fontFamily: 'monospace',
                fontSize: '0.875rem',
              },
            }}
          />
          <Button
            variant="contained"
            startIcon={<SendIcon />}
            onClick={handleEmitEvent}
            disabled={!isConnected || !eventName.trim()}
            sx={{ alignSelf: 'flex-start' }}
          >
            Send Event
          </Button>
        </Box>
      </Paper>

      <Divider />

      {/* Event History */}
      <Box>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
            Event History ({filteredEvents.length})
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Chip
              label={eventFilter === 'all' ? 'All' : eventFilter === 'sent' ? 'Sent' : 'Received'}
              size="small"
              onClick={() => {
                if (eventFilter === 'all') setEventFilter('sent');
                else if (eventFilter === 'sent') setEventFilter('received');
                else setEventFilter('all');
              }}
              sx={{ cursor: 'pointer' }}
            />
            <Button
              variant="outlined"
              size="small"
              startIcon={<DeleteIcon />}
              onClick={handleClearHistory}
              disabled={allEvents.length === 0}
            >
              Clear
            </Button>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, maxHeight: 400, overflow: 'auto' }}>
          {filteredEvents.length > 0 ? (
            filteredEvents.map((event, index) => {
              const isExpanded = expandedEvents.has(index);
              return (
                <Paper
                  key={index}
                  sx={{
                    p: 1.5,
                    border: '1px solid',
                    borderColor: 'divider',
                    borderLeft: `4px solid ${getEventColor(event.type)}`,
                    borderRadius: 1,
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                    <IconButton
                      size="small"
                      onClick={() => toggleExpand(index)}
                      sx={{ p: 0.5 }}
                    >
                      {isExpanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                    </IconButton>

                    <Box sx={{ flex: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 0.5 }}>
                        <Chip
                          label={event.type === 'sent' ? 'SENT' : 'RECEIVED'}
                          size="small"
                          sx={{
                            bgcolor: getEventColor(event.type),
                            color: 'white',
                            fontFamily: 'monospace',
                            fontSize: '0.7rem',
                            fontWeight: 600,
                          }}
                        />
                        <Typography
                          variant="body2"
                          sx={{
                            fontFamily: 'monospace',
                            fontWeight: 600,
                            color: 'primary.main',
                          }}
                        >
                          {event.event.replace('emit:', '')}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
                          {formatTimestamp(event.timestamp)}
                        </Typography>
                        <IconButton
                          size="small"
                          onClick={() => copyEventData(event)}
                          sx={{ p: 0.5 }}
                          title="Copy payload"
                        >
                          <CopyIcon fontSize="small" />
                        </IconButton>
                      </Box>

                      <Collapse in={isExpanded}>
                        <Box
                          sx={{
                            mt: 1,
                            p: 1.5,
                            bgcolor: 'background.default',
                            borderRadius: 1,
                            fontFamily: 'monospace',
                            fontSize: '0.75rem',
                            maxHeight: 200,
                            overflow: 'auto',
                          }}
                        >
                          <pre style={{ margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                            {formatData(event.data)}
                          </pre>
                        </Box>
                      </Collapse>
                    </Box>
                  </Box>
                </Paper>
              );
            })
          ) : (
            <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', p: 2, textAlign: 'center' }}>
              No events captured yet
            </Typography>
          )}
        </Box>
      </Box>
    </Box>
  );
};

